<template>
  <SlideOver :open="on" @close="close">
    <div class="w-screen max-w-md h-full">
      <form
        @submit.prevent="storeUser"
        class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl"
      >
        <div class="flex-1 h-0 overflow-y-auto">
          <div class="py-6 px-4 bg-light-blue-700 sm:px-6">
            <div class="flex items-center justify-between">
              <h2
                id="slide-over-heading"
                class="text-lg font-medium text-white"
              >
                {{ t("user_create") }}
              </h2>
              <div class="ml-3 h-7 flex items-center">
                <button
                  type="button"
                  @click="close()"
                  class="
                    bg-light-blue-700
                    rounded-md
                    focus:outline-none focus:ring-2 focus:ring-white
                  "
                >
                  <XIcon class="h-6 w-6 hover:text-white text-light-blue-200" />
                </button>
              </div>
            </div>
            <div class="mt-1">
              <p class="text-sm text-light-blue-300">
                {{ t("description") }}
              </p>
            </div>
          </div>
          <div class="flex-1 flex flex-col justify-between">
            <div class="px-4 divide-y divide-gray-200 sm:px-6">
              <div class="space-y-6 pt-6 pb-5">
                <Link
                  @click="
                    form.clearErrors();
                    form.reset();
                  "
                  :href="
                    route('users.create', {
                      search: $page.props.filters.search,
                    })
                  "
                  v-if="existingUser"
                  :class="{
                    'border-red-600 hover:border-red-600':
                      'email' in form.errors,
                  }"
                  class="
                    relative
                    rounded-lg
                    border border-gray-300
                    bg-white
                    px-6
                    py-5
                    shadow-sm
                    flex
                    items-center
                    space-x-3
                    hover:border-gray-400
                    focus-within:ring-2
                    focus-within:ring-offset-2
                    focus-within:ring-light-blue-500
                  "
                >
                  <div class="flex-shrink-0">
                    <ui-avatar
                      backgroundStyling="bg-light-blue-400"
                      textStyling="text-white"
                      sizeStyling="h-10 w-10"
                    />
                  </div>
                  <div class="flex-1 min-w-0">
                    <a href="#" class="focus:outline-none">
                      <span class="absolute inset-0" aria-hidden="true"></span>
                      <p class="text-sm font-medium text-gray-900 truncate">
                        {{ existingUser.data.name }}
                      </p>
                      <p class="text-sm text-gray-500 truncate">
                        {{ existingUser.data.email }}
                      </p>
                      <div class="flex flex-wrap items-center gap-2 mt-2">
                        <span
                          v-for="account in existingUser.data.accounts.data"
                          :key="account.id"
                          class="
                            inline-flex
                            items-center
                            px-2.5
                            py-0.5
                            rounded-full
                            text-xs
                            font-medium
                            bg-light-blue-100
                            text-light-blue-800
                            capitalize
                          "
                        >
                          {{ t(account.type) }}
                        </span>
                      </div>
                    </a>
                  </div>
                </Link>
                <div
                  class="flex space-x-1"
                  v-if="'email' in form.errors && existingUser"
                >
                  <ExclamationCircleIcon class="h-5 w-5 text-red-500" />
                  <p class="text-red-600 text-sm">
                    {{ form.errors.email }}
                  </p>
                </div>
                <ui-input
                  v-show="!existingUser"
                  ref="email"
                  :invalid="'email' in form.errors"
                  :error="form.errors.email"
                  type="text"
                  :label="t('email')"
                  v-model="form.email"
                  :disabled="email != undefined"
                ></ui-input>

                <template v-if="email">
                  <ui-input
                    v-show="!existingUser"
                    :invalid="'name' in form.errors"
                    :error="form.errors.name"
                    type="text"
                    :label="t('name')"
                    v-model="form.name"
                  ></ui-input>

                  <ui-select
                    :options="accountTypeOptions"
                    :placeholder="t('select_account_type')"
                    :invalid="'account_type' in form.errors"
                    :error="form.errors.account_type"
                    :label="t('account_type')"
                    v-model="form.account_type"
                  ></ui-select>

                  <template v-if="roleInputVisibility">
                    <ui-select
                      :options="roleOptions"
                      :placeholder="t('select_role')"
                      :invalid="'role' in form.errors"
                      :error="form.errors.role"
                      :label="t('role')"
                      v-model.number="form.role"
                      :cornerHint="t('optional')"
                    ></ui-select>
                  </template>
                </template>
              </div>
            </div>
          </div>
        </div>
        <div class="flex-shrink-0 px-4 py-4 flex justify-end">
          <ui-button
            backgroundStyling="bg-white"
            borderStyling="border border-gray-300"
            textColorStyling="text-gray-700 hover:text-gray-500"
            type="button"
            @click="close"
            >{{ t("cancel") }}</ui-button
          >

          <ui-button
            :disabled="form.processing"
            v-if="email"
            type="submit"
            class="ml-4"
          >
            {{ t("save") }}
          </ui-button>

          <ui-button
            :disabled="form.processing"
            v-if="!email"
            type="submit"
            class="ml-4"
          >
            {{ t("continue") }}
          </ui-button>
        </div>
      </form>
    </div>
  </SlideOver>
</template>

<script>
import { XIcon, ExclamationCircleIcon } from "@heroicons/vue/outline";
import { useI18n } from "vue-i18n";
import SlideOver from "@/Shared/SlideOver";

export default {
  emits: ["toggleVisibility"],

  components: {
    XIcon,
    ExclamationCircleIcon,
    SlideOver,
  },
  props: {
    on: { type: Boolean, required: true },
    accountTypes: { type: Array, required: false },
    roles: { type: Object, required: false },
    email: { type: String, required: false },
    existingUser: { type: Object, required: false },
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  mounted() {
    if (this.on) {
      setTimeout(() => {
        this.$refs.email.$refs.input.focus();
      }, 250);
    }
  },

  computed: {
    accountTypeOptions() {
      if (this.accountTypes.length > 0) {
        return this.accountTypes.map((type) => {
          return {
            text: this.t(type),
            value: type,
          };
        });
      }
      return [];
    },

    roleOptions() {
      if (this.roles.data.length > 0) {
        return this.roles.data.map((role) => {
          return {
            text: role.name,
            value: role.id,
          };
        });
      }
      return [];
    },

    roleInputVisibility() {
      return this.form.account_type == "employee";
    },

    capacityInputVisibility() {
      return this.form.account_type == "coach";
    },
  },

  data() {
    return {
      form: this.$inertia.form({
        _method: "POST",
        name: "",
        email: this.email,
        account_type: "",
        role: "",
      }),
    };
  },

  methods: {
    storeUser() {
      if (!this.email) {
        this.$inertia.visit(route("users.create"), {
          data: {
            search: this.$page.props.filters.search,
            email: this.form.email,
          },
        });
      } else {
        var address = "employees.store";
        switch (this.form.account_type) {
          case "administrator":
            address = "administrators.store";
            break;
          case "employee":
            address = "employees.store";
            break;
          case "technician":
            address = "technicians.store";
          case "technician":
            address = "technicians.store";
        }

        if (this.existingUser) {
          this.form.name = this.existingUser.data.name;
          this.form.email = this.existingUser.data.email;
        }

        this.form.search = this.$page.props.filters.search;

        this.form.post(
          this.route(address, {
            search: this.$page.props.filters.search,
          }),
          {
            preserveScroll: true,
            onSuccess: (page) => {
              this.close();
            },
          }
        );
      }
    },

    close() {
      this.form.reset();
      this.form.clearErrors();
      this.$emit("toggleVisibility");
    },
  },

  watch: {
    on: {
      handler: (on) => {
        if (on) {
          setTimeout(() => {
            this.$refs.email.$refs.input.focus();
          }, 250);
        }
      },
    },

    email: {
      handler: (email) => {
        if (email) {
          this.form.email = this.email;
        }
      },
    },
  },
};
</script>


<i18n>
{
  "es": {
    "administrator": "Administrador",
    "employee": "Empleado",
    "technician": "Signatario",
    "user_create": "Agregar personal",
    "description": "Establece la información de la cuenta a agregar",
    "name": "Nombre",
    "email": "Correo electrónico",
    "optional": "opcional",
    "account_type": "Tipo de cuenta",
    "select_account_type": "Selecciona un tipo de cuenta",
    "save": "Guardar",
    "continue": "Continuar",
    "cancel": "Cancelar",
    "role": "Rol y permisos",
    "select_role": "Selecciona un rol"
  }
}
</i18n>
